export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

async function request(method, path, body, config = {}) {
  const headers = { ...(config.headers || {}) };
  const token = localStorage.getItem("riq_token");
  if (token && !headers["Authorization"]) headers["Authorization"] = `Bearer ${token}`;
  let payload;
  const isUpload = body instanceof FormData;
  if (isUpload) {
    payload = body; // let browser set multipart boundary
    delete headers["Content-Type"];
  } else if (body !== undefined && body !== null) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  // Never let a stuck request hang the UI forever. Uploads/AI/downloads can be slow, so allow longer.
  const wantsBlob = config.responseType === "blob" || config.responseType === "arraybuffer";
  const timeoutMs = config.timeout ?? (isUpload || wantsBlob ? 120000 : 30000);
  const controller = new AbortController();
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      credentials: "include",
      headers,
      body: payload,
      signal: controller.signal,
    });
  } catch (e) {
    const err = new Error(e.name === "AbortError" ? "The request timed out. Please try again." : "Network error. Please check your connection.");
    err.response = { status: 0, data: null };
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
  // Binary downloads (e.g. PDF reports): read as a Blob, never as text (text() corrupts binary).
  if (wantsBlob && res.ok) {
    const blob = await res.blob();
    return { data: blob, status: res.status };
  }
  const ct = res.headers.get("content-type") || "";
  let data = null;
  if (ct.includes("application/json")) {
    data = await res.json().catch(() => null);
  } else {
    data = await res.text().catch(() => null);
  }
  if (!res.ok) {
    const err = new Error(`Request failed with status ${res.status}`);
    err.response = { status: res.status, data };
    throw err;
  }
  return { data, status: res.status };
}

const api = {
  get: (path, config) => request("GET", path, undefined, config),
  post: (path, body, config) => request("POST", path, body, config),
  patch: (path, body, config) => request("PATCH", path, body, config),
  put: (path, body, config) => request("PUT", path, body, config),
  delete: (path, config) => request("DELETE", path, undefined, config),
};

export async function downloadFile(path, filename) {
  const token = localStorage.getItem("riq_token");
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default api;
